/**
 * As operações SQL, sem React e sem rede.
 *
 * Quem decide é o serviço: se o SQL é leitura ou escrita, se ele é aceito, se a
 * escrita está aprovada. A tela só adianta o que dá para ver no texto (os
 * parâmetros, o tipo provável) e monta o corpo que o contrato pede.
 *
 * Os parâmetros são por POSIÇÃO no serviço e por NOME no CRM: o primeiro da
 * lista é o primeiro marcador do SQL (`@p0` no SQL Server, `$1` no PostgreSQL,
 * o primeiro `?` no MySQL). A lista nasce do SQL e só o nome, o tipo e o valor
 * de teste são da pessoa.
 */

import type { ConexaoDaApi, OperacaoDaApi, SalvarOperacao } from '@cliqcrm/contracts';
import type { TomSelo } from '@/shared/ui';

export type Operacao = OperacaoDaApi;
export type ColunaDeSaida = NonNullable<Operacao['colunas']>[number];
export type Finalidade = Operacao['finalidade'];
export type Familia = ConexaoDaApi['familia'];
export type TipoDoParametro = 'texto' | 'numero' | 'data' | 'booleano';

export const FINALIDADES: Record<Finalidade, { rotulo: string; apoio: string }> = {
  consulta: { rotulo: 'Consulta', apoio: 'Lida pelas automações, linha a linha.' },
  listagem: { rotulo: 'Listagem', apoio: 'Uma lista que a empresa vê em Listas.' },
  acao: { rotulo: 'Ação de card', apoio: 'Uma escrita disparada por um botão no card.' },
};

export const TIPOS_DO_PARAMETRO: Record<TipoDoParametro, string> = {
  texto: 'Texto',
  numero: 'Número',
  data: 'Data',
  booleano: 'Sim ou não',
};

export const TIPOS_DA_COLUNA: Record<ColunaDeSaida['tipo'], string> = {
  texto: 'Texto',
  numero: 'Número',
  data: 'Data',
  booleano: 'Sim ou não',
};

/** A situação como a pessoa lê: a escrita em rascunho é a que espera aprovação. */
export function situacaoDe(o: Pick<Operacao, 'situacao' | 'tipo'>): {
  rotulo: string;
  tom: TomSelo;
} {
  if (o.situacao === 'publicada') return { rotulo: 'Publicada', tom: 'ok' };
  if (o.situacao === 'recusada') return { rotulo: 'Recusada', tom: 'erro' };
  return o.tipo === 'escrita'
    ? { rotulo: 'Aguarda aprovação', tom: 'atencao' }
    : { rotulo: 'Rascunho', tom: 'rascunho' };
}

/**
 * O travado do botão: com motivo, desabilitado e dizendo por quê; sem, solto.
 * O `Botao` exige os dois juntos.
 */
export function travar(motivo: string | null): { desabilitado: boolean; motivo: string } {
  return { desabilitado: motivo !== null, motivo: motivo ?? '' };
}

// ── O que se lê no SQL ───────────────────────────────────────────────────

/**
 * O SQL sem o que não é código: textos entre aspas e comentários viram
 * espaço do mesmo tamanho, para um `@p0` dentro de um texto não contar.
 */
function soOCodigo(sql: string): string {
  return sql.replace(/'(?:[^']|'')*'|--[^\n]*|\/\*[\s\S]*?\*\//g, (trecho) =>
    trecho.replace(/[^\n]/g, ' '),
  );
}

export interface ParametrosDoSql {
  /** Quantos parâmetros o SQL usa, na ordem das posições. */
  readonly quantos: number;
  /** As posições que faltam no meio (`@p0` e `@p2` sem `@p1`). */
  readonly faltando: readonly number[];
}

/** Os parâmetros que o SQL usa, pelo marcador da família do banco. */
export function parametrosDoSql(sql: string, familia: Familia): ParametrosDoSql {
  const codigo = soOCodigo(sql);
  if (familia === 'mysql') {
    return { quantos: (codigo.match(/\?/g) ?? []).length, faltando: [] };
  }
  const marcador = familia === 'sqlserver' ? /@p(\d+)\b/gi : /\$(\d+)\b/g;
  const base = familia === 'sqlserver' ? 0 : 1;
  const posicoes = new Set<number>();
  for (const m of codigo.matchAll(marcador)) posicoes.add(Number(m[1]) - base);
  if (posicoes.size === 0) return { quantos: 0, faltando: [] };
  const maior = Math.max(...posicoes);
  const faltando: number[] = [];
  for (let i = 0; i <= maior; i++) if (!posicoes.has(i)) faltando.push(i);
  return { quantos: maior + 1, faltando };
}

/** Como a família escreve o parâmetro de uma posição, para a tela mostrar. */
export function marcadorDe(posicao: number, familia: Familia): string {
  if (familia === 'sqlserver') return `@p${posicao}`;
  if (familia === 'postgres') return `$${posicao + 1}`;
  return `${posicao + 1}º ?`;
}

/**
 * O tipo provável, pelo comando: o serviço é quem decide, e a tela só avisa
 * antes de salvar que a escrita vai esperar aprovação.
 */
export function tipoProvavel(sql: string): Operacao['tipo'] | null {
  const codigo = soOCodigo(sql).trim().toLowerCase();
  if (!codigo) return null;
  if (/\b(insert|update|delete)\b/.test(codigo)) return 'escrita';
  if (/^(select|with)\b/.test(codigo)) return 'leitura';
  return null;
}

// ── O formulário ─────────────────────────────────────────────────────────

export interface Parametro {
  readonly nome: string;
  readonly tipo: TipoDoParametro;
  readonly valorDeTeste: string;
}

export interface Formulario {
  readonly nome: string;
  readonly conexaoId: string;
  readonly finalidade: Finalidade;
  readonly sql: string;
  readonly parametros: readonly Parametro[];
  readonly colunas: readonly ColunaDeSaida[];
  readonly maximoDeLinhas: string;
  readonly tempoMaximoMs: string;
}

export const LIMITES = {
  linhas: { min: 1, max: 10_000, padrao: 500 },
  tempo: { min: 100, max: 30_000, padrao: 5_000 },
} as const;

export function formularioVazio(conexaoId: string, finalidade: Finalidade): Formulario {
  return {
    nome: '',
    conexaoId,
    finalidade,
    sql: '',
    parametros: [],
    colunas: [],
    maximoDeLinhas: String(LIMITES.linhas.padrao),
    tempoMaximoMs: String(LIMITES.tempo.padrao),
  };
}

function tipoConhecido(tipo: string): TipoDoParametro {
  return tipo in TIPOS_DO_PARAMETRO ? (tipo as TipoDoParametro) : 'texto';
}

export function formularioDe(o: Operacao): Formulario {
  return {
    nome: o.nome,
    conexaoId: o.conexao_id,
    finalidade: o.finalidade,
    sql: o.sql,
    parametros: o.parametros.map((p) => ({
      nome: p.nome,
      tipo: tipoConhecido(p.tipo),
      valorDeTeste: '',
    })),
    colunas: o.colunas ?? [],
    maximoDeLinhas: String(o.limites.maximo_de_linhas),
    tempoMaximoMs: String(o.limites.tempo_maximo_ms),
  };
}

/**
 * A lista de parâmetros do tamanho que o SQL pede: o que a pessoa já escreveu
 * fica na mesma posição, e a posição nova nasce `p<n>`.
 */
export function ajustarParametros(
  atuais: readonly Parametro[],
  quantos: number,
): readonly Parametro[] {
  if (atuais.length === quantos) return atuais;
  return Array.from(
    { length: quantos },
    (_, i) => atuais[i] ?? { nome: `p${i}`, tipo: 'texto', valorDeTeste: '' },
  );
}

const NOME_DO_PARAMETRO = /^[a-z_][a-z0-9_]{0,63}$/;

export type Problemas = Partial<Record<string, string>>;

function inteiroEntre(valor: string, min: number, max: number): number | null {
  if (!/^\d+$/.test(valor.trim())) return null;
  const n = Number(valor);
  return n >= min && n <= max ? n : null;
}

export function problemasDoFormulario(f: Formulario): Problemas {
  const p: Problemas = {};
  if (!f.nome.trim()) p['nome'] = 'Dê um nome que diga o que a operação traz ou faz.';
  if (!f.conexaoId) p['conexao'] = 'Escolha de qual banco a operação lê.';
  if (!f.sql.trim()) p['sql'] = 'Escreva o SQL.';

  const vistos = new Set<string>();
  f.parametros.forEach((parametro, i) => {
    if (!NOME_DO_PARAMETRO.test(parametro.nome)) {
      p[`parametro.${i}`] = 'Use letras minúsculas, números e _, começando por letra.';
    } else if (vistos.has(parametro.nome)) {
      p[`parametro.${i}`] = 'Outro parâmetro já tem este nome.';
    }
    vistos.add(parametro.nome);
  });

  const colunas = new Set<string>();
  f.colunas.forEach((coluna, i) => {
    const nome = coluna.nome.trim();
    if (!nome) p[`coluna.${i}`] = 'Dê o nome da coluna, como ela vem no SELECT.';
    else if (colunas.has(nome)) p[`coluna.${i}`] = 'Esta coluna já foi declarada.';
    colunas.add(nome);
  });

  // A lista mostra as colunas declaradas: listagem sem coluna é uma lista sem nada.
  if (f.finalidade === 'listagem' && f.colunas.length === 0) {
    p['colunas'] = 'Uma listagem precisa das colunas de saída: são elas que a lista mostra.';
  }

  const { linhas, tempo } = LIMITES;
  if (inteiroEntre(f.maximoDeLinhas, linhas.min, linhas.max) === null) {
    p['maximoDeLinhas'] = `De ${linhas.min} a ${linhas.max.toLocaleString('pt-BR')} linhas.`;
  }
  if (inteiroEntre(f.tempoMaximoMs, tempo.min, tempo.max) === null) {
    p['tempoMaximoMs'] = `De ${tempo.min} a ${tempo.max.toLocaleString('pt-BR')} ms.`;
  }
  return p;
}

/** O corpo de criar e de editar. Sem coluna declarada, `colunas` vai nulo. */
export function corpoParaSalvar(f: Formulario): SalvarOperacao {
  return {
    conexao_id: f.conexaoId,
    nome: f.nome.trim(),
    sql: f.sql,
    finalidade: f.finalidade,
    parametros: f.parametros.map((p) => ({ nome: p.nome, tipo: p.tipo })),
    colunas: f.colunas.length === 0 ? null : f.colunas.map((c) => ({ ...c, nome: c.nome.trim() })),
    limites: {
      maximo_de_linhas: Number(f.maximoDeLinhas),
      tempo_maximo_ms: Number(f.tempoMaximoMs),
    },
  };
}

/** O que a pessoa mudou em relação à operação salva. */
export function mudou(f: Formulario, salva: Operacao | null): boolean {
  if (!salva) return true;
  const corpo = corpoParaSalvar(f);
  const atual = corpoParaSalvar(formularioDe(salva));
  return JSON.stringify(corpo) !== JSON.stringify(atual);
}

// ── O teste ──────────────────────────────────────────────────────────────

export type ValorDoParametro = string | number | boolean | null;

/**
 * Os valores de teste, por nome, no tipo de cada parâmetro. O que não se
 * converte vira problema, e o teste não vai ao serviço.
 */
export function valoresDeTeste(parametros: readonly Parametro[]): {
  valores: Record<string, ValorDoParametro>;
  problemas: Problemas;
} {
  const valores: Record<string, ValorDoParametro> = {};
  const problemas: Problemas = {};
  parametros.forEach((p, i) => {
    const bruto = p.valorDeTeste.trim();
    if (!bruto) {
      problemas[`teste.${i}`] = 'Dê um valor para testar.';
      return;
    }
    if (p.tipo === 'numero') {
      const n = Number(bruto.replace(',', '.'));
      if (Number.isFinite(n)) valores[p.nome] = n;
      else problemas[`teste.${i}`] = 'Use um número, como 10 ou 2,5.';
    } else if (p.tipo === 'booleano') {
      const v = bruto.toLowerCase();
      if (v === 'sim' || v === 'true' || v === '1') valores[p.nome] = true;
      else if (v === 'não' || v === 'nao' || v === 'false' || v === '0') valores[p.nome] = false;
      else problemas[`teste.${i}`] = 'Use sim ou não.';
    } else if (p.tipo === 'data') {
      if (/^\d{4}-\d{2}-\d{2}( \d{2}:\d{2}(:\d{2})?)?$/.test(bruto)) valores[p.nome] = bruto;
      else problemas[`teste.${i}`] = 'Use AAAA-MM-DD, como 2026-10-22.';
    } else {
      valores[p.nome] = bruto;
    }
  });
  return { valores, problemas };
}

const DATA_ISO = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:?\d{2})?)?$/;

function tipoDoValor(valor: unknown): ColunaDeSaida['tipo'] | null {
  if (valor === null || valor === undefined) return null;
  if (typeof valor === 'number') return 'numero';
  if (typeof valor === 'boolean') return 'booleano';
  if (typeof valor === 'string' && DATA_ISO.test(valor)) return 'data';
  return 'texto';
}

/**
 * As colunas de saída tiradas das linhas de um teste: o nome como veio, o tipo
 * do primeiro valor não vazio, e "aceita vazio" quando alguma linha veio sem.
 */
export function colunasDoResultado(
  linhas: readonly Record<string, unknown>[],
): readonly ColunaDeSaida[] {
  const nomes: string[] = [];
  for (const linha of linhas)
    for (const nome of Object.keys(linha)) {
      if (!nomes.includes(nome)) nomes.push(nome);
    }
  return nomes.map((nome) => {
    const valores = linhas.map((l) => l[nome]);
    const tipo = valores.map(tipoDoValor).find((t) => t !== null) ?? 'texto';
    const vazio = valores.some((v) => v === null || v === undefined);
    return { nome, tipo, ...(vazio ? { aceita_vazio: true } : {}) };
  });
}

/** Um valor do resultado como texto de célula. */
export function celula(valor: unknown): string {
  if (valor === null || valor === undefined) return '—';
  if (typeof valor === 'boolean') return valor ? 'sim' : 'não';
  if (typeof valor === 'object') return JSON.stringify(valor);
  return String(valor);
}

// ── A aprovação ──────────────────────────────────────────────────────────

/**
 * A mesma regra do `podeAprovarOperacao` da API: só a escrita em rascunho. Sem a
 * regra das duas pessoas (ADR-0035, 03/11/2026): a escrita entra no ar ao
 * salvar, e a que ficou em rascunho de antes, qualquer um da Cliqfy aprova,
 * até quem a escreveu. A API confere de novo.
 */
export function porQueNaoAprova(
  o: Operacao,
  meuId: string | null,
  papel: string | null = null,
): string | null {
  if (o.tipo !== 'escrita') return 'A leitura é publicada ao salvar: não passa por aprovação.';
  if (o.situacao !== 'rascunho') return null;
  if (!meuId) return 'Sem sessão: entre de novo.';
  void papel;
  return null;
}

export function esperaAprovacao(o: Operacao): boolean {
  return o.tipo === 'escrita' && o.situacao === 'rascunho';
}

/** As escritas que esperam a aprovação DESTA pessoa: as que ela não escreveu (o Super Admin, todas). */
export function esperamMinhaAprovacao(
  operacoes: readonly Operacao[],
  meuId: string | null,
  papel: string | null = null,
): readonly Operacao[] {
  return operacoes.filter((o) => esperaAprovacao(o) && porQueNaoAprova(o, meuId, papel) === null);
}

// ── A lista e o histórico ────────────────────────────────────────────────

export function filtrar(
  operacoes: readonly Operacao[],
  finalidade: Finalidade | null,
  busca: string,
): readonly Operacao[] {
  const termo = busca.trim().toLowerCase();
  return operacoes.filter(
    (o) =>
      (finalidade === null || o.finalidade === finalidade) &&
      (!termo || o.nome.toLowerCase().includes(termo)),
  );
}

const ROTULO_DO_HISTORICO: Readonly<Record<string, string>> = {
  criada: 'Criada',
  editada: 'Alterada',
  aprovada: 'Aprovada e publicada',
  recusada: 'Recusada',
  executada: 'Executada ao vivo',
};

export function oQueAconteceu(acao: string): string {
  return ROTULO_DO_HISTORICO[acao] ?? 'Alterada';
}

export function quemFez(autorId: string, meuId: string | null): string {
  return meuId !== null && autorId.toLowerCase() === meuId.toLowerCase()
    ? 'Você'
    : 'Outra pessoa da Cliqfy';
}
